<template>
  <div class="time-entries text-center">
    <TopMenu
      :back-to="photoBackTo"
      :title="photoTitle"
      dark-background/>
    <div class="container-fluid content">
      <croppa
        v-if="isAdd"
        v-model="localCroppaObject"
        :placeholder="$t('PHOTO.IMPORT')"
        :prevent-white-space="true"
        :show-remove-button="false"
        :disabled="false"
        :placeholder-font-size="22"
        :placeholder-color="'#0C2340'"
        :width="270"
        :height="202"
        initial-size="cover"
        class="mt-3"/>
      <PhotoContainer
        v-else
        :src="selectedAttachedFileUrl"
        :show="selectedAttachedFile?true:false"
        class="mt-3"/>
      <FormTextArea
        v-model="localPhoto.description"
        :placeholder="$t('PHOTO.DESCRIPTION_LABEL')"
        :disabled="!isAdd"
        class="mt-3"
      />
      <ActionButton
        v-if="isAdd && !selectedTimeEntry.isSubmitted"
        class="btn btn-lg btn-primary btn-block mt-3 mb-3"
        @click="generateCroppaPhoto"
      >{{ $t("PHOTO.ADD_BUTTON") }}</ActionButton>
      <ActionButton
        v-if="!isAdd && !selectedTimeEntry.isSubmitted"
        class="btn btn-lg btn-danger btn-block mt-3 mb-3"
        @click="showConfirmDelete = true"
      >{{ $t("PHOTO.DELETE_BUTTON") }}</ActionButton>
      <div class="text-justify mt-4 ml-3 mr-3 photo-note">
        <p>{{ $t("PHOTO.PHOTOS_12_MONTHS") }}</p>
      </div>
    </div>
    <BottomMenu/>
    <ConfirmModal
      v-if="showConfirmDelete"
      show-modal
      size="md"
      @cancel="showConfirmDelete = false"
      @confirm="confirmDelete"
    >{{ $t("PHOTO.CONFIRM_MODAL_DELETE_PHOTO") }}</ConfirmModal>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import TopMenu from '@/components/Menu/TopMenu';
import BottomMenu from '@/components/Menu/BottomMenu';
import ActionButton from '@/components/Actions/ActionButton';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import FormTextArea from '@/components/Forms/FormTextArea';
import PhotoContainer from '@/components/Utils/PhotoContainer';
import UrlHelper from '@/helpers/url-helper';

export default {
  name: 'ExpensePhoto',
  components: {
    ActionButton,
    TopMenu,
    BottomMenu,
    ConfirmModal,
    FormTextArea,
    PhotoContainer,
  },
  mixins: [UrlHelper],
  data() {
    return {
      localCroppaObject: {},
      localPhoto: {
        photo: null,
        description: '',
      },
      showConfirmDelete: false,
    };
  },
  computed: {
    ...mapState('expense', ['selectedExpense']),
    ...mapState('timeEntry', ['selectedTimeEntry']),
    ...mapState('attachedFile', ['selectedAttachedFile']),
    photoType() {
      let t = '';
      if (this.$route.params.expenseId) t = 'expense';
      else if (this.$route.params.timeEntryId) t = 'timeEntry';
      return t;
    },
    isAdd() {
      return this.$route.params.photoId === 'add';
    },
    photoBackTo() {
      let backTo = '/dashboard/time-entries';
      if (this.photoType === 'expense') {
        backTo = `/dashboard/time-entries/${
          this.$route.params.timeEntryId
        }/expense/${this.$route.params.expenseId}`;
      } else if (this.photoType === 'timeEntry') {
        backTo = `/dashboard/time-entries/${this.$route.params.timeEntryId}`;
      }
      return backTo;
    },
    photoTitle() {
      let title = this.$t('PHOTO.PAGE_LABEL_ADD');
      if (!this.isAdd && this.selectedTimeEntry.isSubmitted) {
        title = this.$t('PHOTO.PAGE_LABEL');
      } else if (!this.isAdd && this.selectedTimeEntry.isSubmitted) {
        title = this.$t('PHOTO.PAGE_LABEL_EDIT');
      }
      return title;
    },
    selectedAttachedFileUrl() {
      return (this.selectedAttachedFile
        ? `data:${this.selectedAttachedFile.contentType};base64,${this.selectedAttachedFile.file}`
        : ''
      );
    },
  },
  watch: {
    selectedExpense: {
      handler(newVal) {
        if (this.photoType === 'expense') this.handlePhoto(newVal);
      },
      immediate: true,
    },
    selectedAttachedFile: {
      handler(newVal) {
        if (this.photoType === 'timeEntry') this.handlePhoto(newVal);
      },
      immediate: true,
    },
  },
  created() {
    this.$store.dispatch('attachedFile/resetAttachedFile');
    this.$store.dispatch('expense/resetExpense');
    if (this.$route.params && this.$route.params.timeEntryId) {
      this.$store.dispatch(
        'timeEntry/timeEntryById',
        this.$route.params.timeEntryId,
      );
    }
    if (this.photoType === 'expense') {
      if (!this.isAdd) {
        this.$store
          .dispatch('expense/expense', {
            timeEntryId: this.$route.params.timeEntryId,
            expenseId: this.$route.params.expenseId,
          })
          .then(() => {
            this.$store.dispatch(
              'attachedFile/attachedFile',
              this.$route.params.photoId,
            );
          });
      }
    } else if (this.photoType === 'timeEntry') {
      if (!this.isAdd) {
        this.$store.dispatch(
          'attachedFile/attachedFile',
          this.$route.params.photoId,
        );
      }
    }
  },
  methods: {
    generateCroppaPhoto() {
      this.localPhoto.photo = null;
      if (this.localCroppaObject.chosenFile) {
        const name = this.localCroppaObject.chosenFile.name; //eslint-disable-line
        this.localCroppaObject.generateBlob((blob) => {
          this.localPhoto.photo = new File([blob], name);
          this.add();
        }, this.localCroppaObject.chosenFile.type, 0.8);
      } else {
        this.$store.commit('alerts/setAlert', {
          msg: 'PHOTO.NO_PHOTO_SELECTED',
        });
      }
    },
    add() {
      if (this.localPhoto && this.localPhoto.photo) {
        if (this.photoType === 'expense') {
          this.$store.dispatch('expense/addExpensePhoto', {
            data: this.localPhoto,
            timeEntryId: this.$route.params.timeEntryId,
            expenseId: this.$route.params.expenseId,
          });
        } else if (this.photoType === 'timeEntry') {
          this.$store.dispatch('timeEntry/addTimeEntryPhoto', {
            data: this.localPhoto,
            timeEntryId: this.$route.params.timeEntryId,
          });
        }
      } else {
        this.$store.commit('alerts/setAlert', {
          msg: 'PHOTO.NO_PHOTO_SELECTED',
        });
      }
    },
    confirmDelete() {
      this.showConfirmDelete = false;
      if (this.photoType === 'expense') {
        this.$store.dispatch('expense/deleteExpensePhoto', {
          photoId: this.$route.params.photoId,
          timeEntryId: this.$route.params.timeEntryId,
          expenseId: this.$route.params.expenseId,
        });
      } else if (this.photoType === 'timeEntry') {
        this.$store.dispatch('timeEntry/deleteTimeEntryPhoto', {
          photoId: this.$route.params.photoId,
          timeEntryId: this.$route.params.timeEntryId,
        });
      }
    },
    handlePhoto(newVal) {
      if (
        this.photoType === 'expense'
        && newVal
        && newVal.attachedFiles
        && newVal.attachedFiles.length > 0
      ) {
        this.localPhoto = cloneDeep(newVal.attachedFiles[0]);
      } else if (this.photoType === 'timeEntry' && newVal) {
        this.localPhoto = cloneDeep(newVal);
      } else {
        this.localPhoto = {
          photo: null,
          description: '',
        };
      }
    },
  },
};
</script>

<style scoped lang="scss">
div {
  &.photo-wrapper {
    border: 1px solid #000;
    border-radius: 10px;
    border-style: dashed;
  }

  &.time-entries {
    height: 100%;
  }
  &.content {
    padding-top: $top-menu-height;
    padding-bottom: $bottom-menu-height;
    background-color: $base;
    min-height: 100%;
  }
  &.fixed-week-selector {
    position: fixed;
    width: 100%;
    z-index: 1001;
    left: 0;
    top: $top-menu-height;
    background-color: $base;
    border-bottom: 2px solid $secondary;
  }
  &.actions {
    text-align: right;
  }
  &.photo-note {
    font-size: 1.2rem;
  }
  &.croppa-container {
    border: 1px solid #000;
    border-radius: 10px;
    border-style: dashed;
    width: 270px;
    height: 202px;
    margin: auto;
    overflow: hidden;
  }
}
</style>
